import type { BridgeControllers, BridgeSeat as Seat } from '../bridge/seats.js';
import {
  type ColourDashGame,
  type ColourDashOptions,
  type ColourDashPhase,
  type DashCard,
  type DashColour,
  type DashEvent,
  colourDashCanCallLast,
  colourDashCanDraw,
  colourDashLegalPlays,
  colourDashNextSeat,
  colourDashTop,
} from './play.js';

/**
 * Everything one seat may see: its own hand, how many cards the others hold, the top of the pile, the colour in force and the direction,
 * and how many cards are left to draw. No other hand and no stock order is ever included, and events carry counts, never cards.
 */
export interface ColourDashView {
  readonly viewer: Seat;
  readonly phase: ColourDashPhase;
  readonly seats: readonly Seat[];
  readonly controllers: BridgeControllers;
  readonly options: ColourDashOptions;
  readonly hand: readonly DashCard[];
  readonly handSizes: Readonly<Partial<Record<Seat, number>>>;
  readonly top: DashCard;
  /** The colour a play must match (the named colour after a wild). */
  readonly colour: DashColour;
  readonly direction: 1 | -1;
  readonly stockCount: number;
  readonly discardCount: number;
  readonly turn: Seat;
  /** The seat that plays after the seat to play. */
  readonly nextSeat: Seat;
  readonly canAct: boolean;
  readonly legalPlays: readonly DashCard[];
  readonly canDraw: boolean;
  /** True when the viewer has just drawn a playable card and may still decline it. */
  readonly canPass: boolean;
  /** True when the viewer holds exactly two cards on their turn and has not yet called "last card". */
  readonly canCallLast: boolean;
  readonly calledLast: boolean;
  readonly winner: Seat | null;
  readonly events: readonly DashEvent[];
}

export function colourDashViewFor(game: ColourDashGame, viewer: Seat): ColourDashView {
  const mine = game.phase === 'play' && game.turn === viewer;
  return {
    viewer,
    phase: game.phase,
    seats: game.seats,
    controllers: game.controllers,
    options: game.options,
    hand: game.hands[viewer] ?? [],
    handSizes: Object.fromEntries(game.seats.map((s) => [s, (game.hands[s] ?? []).length])),
    top: colourDashTop(game),
    colour: game.colour,
    direction: game.direction,
    stockCount: game.stock.length,
    discardCount: game.discard.length,
    turn: game.turn,
    nextSeat: colourDashNextSeat(game),
    canAct: mine,
    legalPlays: mine ? colourDashLegalPlays(game) : [],
    canDraw: mine && colourDashCanDraw(game),
    canPass: mine && game.drew,
    canCallLast: mine && colourDashCanCallLast(game),
    calledLast: game.calledLast,
    winner: game.winner,
    events: game.events,
  };
}
